/**
 * ShippingProtection — client-side logic that adds the correct variant of
 * the "shipping-protection-benchmark" product to the cart based on the
 * product subtotal.
 *
 * Pricing: 2.75 % of product subtotal, clamped $0.75 – $74.25, rounded up
 * to the nearest whole dollar.
 */
(function () {
  'use strict';

  var PRODUCT_HANDLE = 'shipping-protection-benchmark';
  var RATE           = 0.0275;
  var MIN_DOLLARS    = 0.75;
  var MAX_DOLLARS    = 74.25;
  var CHECKBOX_ID    = 'sp-toggle';
  var MARKER_ATTR    = 'data-sp-owned';

  var _variants      = null;
  var _syncing       = false;
  var _syncQueued    = false;
  /* GAH-EU-LP-01: off until the shopper turns it on in the EU (snippets/eu-market-head).
     The cart remembers the choice: a protection line there on load means they said yes. */
  var _enabled       = !window.GD_SP_DEFAULT_OFF;
  var _spSeeded      = !window.GD_SP_DEFAULT_OFF;
  var _injecting     = false;
  var _syncPromise   = Promise.resolve();
  var _checkoutGuardBusy = false;
  // protection writes (POST /cart/update.js) still unanswered
  let _protectionWrites = 0;
  /* The longest a held Check out tap waits on a sync. Browser fetch has no
     timeout, so a stalled request used to freeze the button until the
     platform gave up (~60s on iOS). Longer than the instant cart's 4s on
     purpose: that wait runs first, and a healthy sync on slow cellular
     (write, verify pause, verify read, maybe a repeat) can pass 4s. */
  const CHECKOUT_HOLD_MS = 10000;

  var ICON_SVG_REDESIGN = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
  <g clip-path="url(#clip0_32013_45781)">
    <rect width="48" height="48" rx="8" fill="#EEEEF1"/>
    <path d="M24.4002 43.2C24.4002 43.2 39.6002 35.68 39.6002 24.4V11.24L24.4002 5.59998L9.2002 11.24V24.4C9.2002 35.68 24.4002 43.2 24.4002 43.2Z" fill="white" stroke="#0a78d4" stroke-width="0.825" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M24.3992 40.0001C24.3992 40.0001 37.1992 33.6001 37.1992 24.0001V12.8001L24.3992 8.00006L11.5992 12.8001V24.0001C11.5992 33.6001 24.3992 40.0001 24.3992 40.0001Z" fill="#0a78d4"/>
    <path d="M23.985 14.9101C24.2942 14.7539 24.6628 14.7539 24.972 14.9101L30.1404 17.5208C30.9048 17.9069 30.9048 18.966 30.1404 19.3522L24.972 21.9629C24.6628 22.1191 24.2942 22.1191 23.985 21.9629L18.8167 19.3522C18.0522 18.966 18.0522 17.9069 18.8167 17.5208L23.985 14.9101Z" fill="white"/>
    <path d="M23.208 23.1969C23.5362 23.3786 23.7387 23.7221 23.7387 24.0971L23.7387 29.2877C23.7387 30.0908 22.8638 30.6063 22.1609 30.2172L17.0435 27.3844C16.7153 27.2027 16.5128 26.8593 16.5128 26.4842L16.5128 21.2941C16.5128 20.491 17.3877 19.9755 18.0906 20.3645L23.208 23.1969Z" fill="white"/>
    <path d="M25.5928 23.1969C25.2646 23.3786 25.0621 23.7221 25.0621 24.0971L25.0621 29.2877C25.0621 30.0908 25.937 30.6063 26.6399 30.2172L31.7572 27.3844C32.0854 27.2027 32.2879 26.8593 32.2879 26.4842L32.2879 21.2941C32.2879 20.491 31.413 19.9755 30.7102 20.3645L25.5928 23.1969Z" fill="white"/>
  </g>
  <defs>
    <clipPath id="clip0_32013_45781">
    <rect width="48" height="48" fill="white"/>
    </clipPath>
  </defs>
</svg>`;

  var ICON_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 40 40" fill="none">'
      + '<rect width="40" height="40" rx="8" fill="#E8F4FA"/>'
      + '<path d="M20 6L8 11V19C8 27 13 33.5 20 35C27 33.5 32 27 32 19V11L20 6Z" fill="#35AFE4"/>'
      + '<path d="M16 20L19 23L25 17" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>'
      + '</svg>';

  /* ------------------------------------------------------------------ */
  /*  Price helpers                                                      */
  /* ------------------------------------------------------------------ */

  function computeProtectionDollars(subtotalCents) {
    if (!subtotalCents || subtotalCents <= 0) return 0;
    var raw = (subtotalCents / 100) * RATE;
    return Math.ceil(Math.min(MAX_DOLLARS, Math.max(MIN_DOLLARS, raw)));
  }

  function computeProtectionCents(subtotalCents) {
    var d = computeProtectionDollars(subtotalCents);
    return d ? d * 100 : 0;
  }

  function formatProtectionPrice(cents) {
    if (typeof formatMoneyLiquidNoZeros === 'function') return formatMoneyLiquidNoZeros(cents);
    if (typeof formatMoneyWithoutTrailingZeros === 'function') return formatMoneyWithoutTrailingZeros(cents);
    if (typeof formatMoney === 'function') return formatMoney(cents, window.money_format);
    return '$' + Math.round(cents / 100);
  }

  /* "for $X" on the widget: the tier the cart will actually be charged (the
     formula's target snapped to a real variant, as syncCart snaps it),
     printed the way Liquid prints the drawer Total beside it - so the label,
     the Total and checkout all describe the same line. The raw formula is the
     fallback until the variant table has loaded. */
  function protectionPriceText(protectableCents) {
    var target = computeProtectionCents(protectableCents);
    if (!target) return '';
    var variant = findBestVariant(target);
    return formatProtectionPrice(variant ? variant.price : target);
  }

  function getProductOnlyTotal(cartState) {
    if (!cartState || !cartState.items) return 0;
    return cartState.items
      .filter(function (item) {
        return !item.handle || !item.handle.includes('shipping-protection');
      })
      .reduce(function (sum, item) {
        return sum + (item.final_line_price || item.line_price || 0);
      }, 0);
  }

  function getProtectableProductTotal(cartState) {
    if (!cartState || !cartState.items) return 0;
    if (!hasProtectableItems(cartState)) return 0;
    return getProductOnlyTotal(cartState);
  }

  /* Every order that ships carries protection - a core part of the product,
     never optional by accident. A Shopify bundle's own line reports
     requires_shipping: false (its components are what ship), so a bundle is
     protectable by has_components: the Launch Bundle ships a mat and a pad
     exactly like Golf At Home on its own. Only a cart with nothing physical
     in it (a coaching plan alone) goes without. */
  function isProtectableLine(item) {
    if (!item) return false;
    const handle = item.handle || '';
    if (handle.includes('shipping-protection') || handle.includes('navidium')) return false;
    return item.requires_shipping === true || isBundleLine(item);
  }

  /* Only GET /cart.js carries has_components; the carts that /cart/update.js
     and /cart/change.js answer with (every protection write, every stepper
     tap) leave it out, so the field alone flickers a bundle between protected
     and not. A variant is a bundle if ANY source says so - the field, the
     instant-cart catalog, or the drawer's own server-rendered row
     (cart-item--has-components) - and it stays one for the page. */
  var _bundleVariants = {};
  // wakes the post-write verification pause early (a Check out tap)
  var _verifyWake = null;
  function isBundleLine(item) {
    var id = String(item.variant_id || item.id || '');
    if (!id) return item.has_components === true;
    if (_bundleVariants[id]) return true;
    var ic = window.InstantCart;
    var info = ic && typeof ic.variant === 'function' ? ic.variant(id) : null;
    var yes = item.has_components === true ||
      !!(info && info.k) ||
      !!document.querySelector('.cart-item--has-components[data-variant-id="' + id + '"]');
    if (yes) _bundleVariants[id] = true;
    return yes;
  }

  function hasProtectableItems(cartState) {
    if (!cartState || !cartState.items) return false;
    return cartState.items.some(isProtectableLine);
  }

  /* ------------------------------------------------------------------ */
  /*  Variant lookup                                                     */
  /* ------------------------------------------------------------------ */

  function findBestVariant(targetCents) {
    if (!_variants || !_variants.length || !targetCents) return null;
    var exact = null, nextUp = null;
    _variants.forEach(function (v) {
      if (!v.available) return;
      if (v.price === targetCents) exact = v;
      else if (v.price > targetCents && (!nextUp || v.price < nextUp.price)) nextUp = v;
    });
    if (exact) return exact;
    if (nextUp) return nextUp;
    var best = null;
    _variants.forEach(function (v) {
      if (!v.available) return;
      if (!best || Math.abs(v.price - targetCents) < Math.abs(best.price - targetCents)) best = v;
    });
    return best;
  }

  /* ------------------------------------------------------------------ */
  /*  UI helpers                                                         */
  /* ------------------------------------------------------------------ */

  function isEnabled() { return _enabled; }

  function updateWidgetPrice(subtotalCents) {
    const protectableSubtotal = window._cartState
      ? getProtectableProductTotal(window._cartState)
      : subtotalCents;
    const text = protectionPriceText(protectableSubtotal);
    document.querySelectorAll('.js-sp-price').forEach(function (el) {
      el.textContent = text;
    });
  }

  function updateWidgetVisibility(cartState) {
    const visible = hasProtectableItems(cartState);
    document.querySelectorAll('.shipping-protection-slot').forEach(function (slot) {
      slot.style.display = visible ? '' : 'none';
    });
  }

  function hideShippingProtectionCartItems() {
    document.querySelectorAll('.cart-item').forEach(function (item) {
      var handle = (item.getAttribute('data-product-handle') || '').toLowerCase();
      var isServiceByHandle = handle.indexOf('shipping-protection') !== -1 || handle.indexOf('navidium') !== -1;
      var link = item.querySelector('a[href*="shipping-protection"], a[href*="navidium"]');
      var isServiceByText = /shipping\s*protection|navidium/i.test(item.textContent || '');
      if (isServiceByHandle || link || isServiceByText) {
        item.remove();
      }
    });
  }

  /* GAH-SPICON-01 arm B (html.gd-sp-box, snippets/oct7-flags): the thing
     being protected, drawn literally - a taped shipping carton with a blue
     shield on its corner - for the "protecting the simulator" angle from the
     Oct 7 meeting. Same 48 box and tile as the redesign icon. */
  var ICON_SVG_BOX = '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">'
      + '<rect width="48" height="48" rx="8" fill="#EEEEF1"/>'
      + '<path d="M8 17.5L22 11L36 17.5V32.5L22 39L8 32.5V17.5Z" fill="#C9965F"/>'
      + '<path d="M8 17.5L22 24V39L8 32.5V17.5Z" fill="#B07D48"/>'
      + '<path d="M22 24L36 17.5V32.5L22 39V24Z" fill="#D6A872"/>'
      + '<path d="M8 17.5L22 11L36 17.5L22 24L8 17.5Z" fill="#E2BC8C"/>'
      + '<path d="M15 14.25L29 20.75V25.5L26 26.9V22.15L12 15.65L15 14.25Z" fill="#F4E3C8"/>'
      + '<path d="M34 25.5C34 25.5 41 22.9 41 22.9L41 30.2C41 35.4 34 38.6 34 38.6C34 38.6 27 35.4 27 30.2V22.9L34 25.5Z" fill="#fff"/>'
      + '<path d="M34 27C34 27 39.4 25 39.4 25V30.3C39.4 34.3 34 36.8 34 36.8C34 36.8 28.6 34.3 28.6 30.3V25L34 27Z" fill="#0a78d4"/>'
      + '<path d="M31.6 30.9L33.3 32.6L36.6 29.3" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>'
      + '</svg>';

  /* ------------------------------------------------------------------ */
  /*  Widget rendering                                                   */
  /* ------------------------------------------------------------------ */

  function buildWidgetHTML() {
    const productTotal = window._cartState ? getProtectableProductTotal(window._cartState) : 0;
    /* the fee is computed in the shop's own currency, so render it through the
       theme money formatter - a hardcoded '$' read as USD in every other market */
    const priceText = protectionPriceText(productTotal);

    const isRedesign = !!document.querySelector('.cart-drawer--redesign');
    const isBoxIcon = document.documentElement.classList.contains('gd-sp-box');
    const iconHTML = isBoxIcon ? ICON_SVG_BOX : (isRedesign ? ICON_SVG_REDESIGN : ICON_SVG);

    return '<div class="sp-widget" ' + MARKER_ATTR + '>'
      + '<div class="sp-widget__icon">' + iconHTML + '</div>'
      + '<div class="sp-widget__text">'
        /* GAH-INTL-HOLIDAY-01: outside the US the title drops "Free Returns" -
           the refund policy has international buyers pay return freight
           (snippets/intl-hol-flags sets GD_SP_TITLE for non-US markets) */
        + '<span class="sp-widget__title">' + (window.GD_SP_TITLE || 'Free Returns &amp; Shipping Protection') + '</span>'
        + '<span class="sp-widget__subtitle">'
          + 'from Damage, Loss &amp; Theft for '
          + '<strong class="js-sp-price">' + priceText + '</strong>'
        + '</span>'
      + '</div>'
      + '<label class="sp-widget__toggle" for="' + CHECKBOX_ID + '">'
        + '<input type="checkbox" id="' + CHECKBOX_ID + '" '
          + 'class="sp-widget__cb"'
          + (_enabled ? ' checked' : '') + '>'
        + '<span class="sp-widget__track"><span class="sp-widget__knob"></span></span>'
      + '</label>'
    + '</div>';
  }

  function injectWidget(slot) {
    if (_injecting) return;
    _injecting = true;
    slot.innerHTML = buildWidgetHTML();
    var cb = slot.querySelector('#' + CHECKBOX_ID);
    if (cb) {
      cb.addEventListener('change', function () {
        _enabled = cb.checked;
        _spSeeded = true;
        onToggle();
      });
    }
    _injecting = false;
  }

  function ensureWidgetIntact() {
    document.querySelectorAll('.shipping-protection-slot').forEach(function (slot) {
      if (slot.querySelector('[' + MARKER_ATTR + ']')) return;
      injectWidget(slot);
    });
  }

  function adjustDisplayedSubtotal(cartState) {
    if (!cartState) return;
    if (window.CartOptimist) {
      CartOptimist.displayForCart(cartState);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Cart sync                                                          */
  /* ------------------------------------------------------------------ */

  function findProtectionItems(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.filter(function (item) {
      return (item.handle || '').includes('shipping-protection');
    });
  }

  function syncCart(optionalCartState) {
    if (!_variants) return Promise.resolve();
    if (_syncing) {
      _syncQueued = true;
      return _syncPromise;
    }

    /* One write leaves exactly one protection line, at quantity 1, of the
       target tier - or none. Every protection line in the cart is checked,
       not just the first: an add that carried protection on a stale cache
       (another tab, a back-navigation) can leave two, or merge one to
       quantity 2, and the old first-line check kept charging the extra. */
    var doSync = function (cart, verified, stale) {
      var productTotal = getProtectableProductTotal(cart);
      var lines        = findProtectionItems(cart);
      if (!_spSeeded) {
        _spSeeded = true;
        if (lines.length) {
          _enabled = true;
          document.querySelectorAll('#' + CHECKBOX_ID).forEach(function (cb) { cb.checked = true; });
        }
      }

      // a read older than the shopper's latest change still decides the repair,
      // but does not repaint the drawer over that change
      if (!stale) {
        adjustDisplayedSubtotal(cart);
        hideShippingProtectionCartItems();
        ensureWidgetIntact();
        updateWidgetVisibility(cart);
      }

      var wanted = _enabled && productTotal > 0;
      var target = wanted ? findBestVariant(computeProtectionCents(productTotal)) : null;
      // no tier to name (table gap): leave the cart as it is
      if (wanted && !target) return Promise.resolve();

      var updates = {};
      var kept = false;
      lines.forEach(function (line) {
        if (target && !kept && String(line.variant_id) === String(target.id)) {
          kept = true;
          if (line.quantity !== 1) updates[line.key] = 1;
          return;
        }
        updates[line.key] = 0;
      });
      if (target && !kept) updates[target.id] = 1;
      if (!Object.keys(updates).length) return Promise.resolve();
      return writeProtection(updates).then(function () {
        refreshCartPageSummary();
        if (verified) return;
        /* Intelligems wraps window.fetch and reads the cart around theme
           writes; a write that lands inside one of those reads can be
           acknowledged and then lost (reproduced: the response shows one
           protection line, the cart a second later shows the old two). So
           every protection write is checked once against a fresh read a
           moment later and repeated if it did not stick - still inside this
           sync, so checkout waits for it. */
        var seqAtRead;
        return new Promise(function (resolve) {
          // a Check out tap cuts the pause short (bindCheckoutSyncGuard)
          var timer = setTimeout(resolve, 1500);
          _verifyWake = function () { clearTimeout(timer); resolve(); };
        })
          .then(function () {
            _verifyWake = null;
            seqAtRead = window._CART_OPTIMISTIC_SEQ;
            return fetch('/cart.js', { cache: 'no-store' }).then(function (r) {
              if (!r.ok) throw new Error('verify read ' + r.status);
              return r.json();
            });
          })
          .then(function (fresh) {
            // attributes that rode the lost write ride the repeat, or go on their own
            if (_lastCarried && !attributesLanded(fresh, _lastCarried)) carryAttributes(_lastCarried);
            _lastCarried = null;
            return doSync(fresh, true, seqAtRead !== window._CART_OPTIMISTIC_SEQ);
          })
          .catch(function (error) {
            // the check is best effort: a failed read or repeat never ends the sync
            // (queued changes still run), and carried attributes go on their own
            console.warn('[ShippingProtection] verify skipped:', error);
            if (_lastCarried) carryAttributes(_lastCarried);
            _lastCarried = null;
          });
      });
    };

    var cartPromise = optionalCartState
      ? Promise.resolve(optionalCartState)
      : fetch('/cart.js', { cache: 'no-store' }).then(function (r) { return r.json(); });

    _syncing = true;
    _syncPromise = cartPromise
      .then(doSync)
      .then(function () {
        _syncing = false;
        if (_syncQueued) {
          _syncQueued = false;
          return syncCart();
        }
      })
      .catch(function (err) {
        console.error('[ShippingProtection] sync error:', err);
        _syncing = false;
        // a change the shopper made while this sync ran still gets its sync
        var rerun = _syncQueued;
        _syncQueued = false;
        if (rerun) return syncCart();
      });

    return _syncPromise;
  }

  /* Every protection write is ONE /cart/update.js: it takes line keys (0 =
     remove) and variant ids (added if absent) in the same call and answers with
     the whole cart, so a tier swap is one round trip instead of change + add +
     GET, and nothing re-reads /cart.js afterwards. Line order is unchanged:
     a variant added here lands on top, where the old add.js put it.
     The state is only applied when no optimistic change happened meanwhile -
     the same staleness rule as updateCartAndProductJSON. */
  /* Cart attributes another writer handed over (carryAttributes) ride along
     with the next protection write instead of costing their own round trip;
     takeCarriedAttributes hands back whatever no write picked up. */
  var _carried = null;

  function carryAttributes(attributes) {
    if (!attributes) return;
    _carried = Object.assign({}, _carried || {}, attributes);
  }

  function takeCarriedAttributes() {
    var left = _carried;
    _carried = null;
    return left;
  }

  // the attributes the last protection write carried, checked by the
  // verification read in doSync
  var _lastCarried = null;

  function attributesLanded(cart, attributes) {
    var have = (cart && cart.attributes) || {};
    return Object.keys(attributes).every(function (k) { return String(have[k] == null ? '' : have[k]) === String(attributes[k]); });
  }

  function writeProtection(updates) {
    var seq = window._CART_OPTIMISTIC_SEQ;
    var body = { updates: updates };
    var sent = null;
    if (_carried) {
      sent = _carried;
      body.attributes = _carried;
      _carried = null;
    }
    _lastCarried = sent;
    _protectionWrites += 1;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('Protection update failed: ' + r.status);
      return r.json();
    }).then(function (cart) {
      window._cartState = cart;
      if (seq === window._CART_OPTIMISTIC_SEQ) adjustDisplayedSubtotal(cart);
      hideShippingProtectionCartItems();
      updateWidgetVisibility(cart);
      return cart;
    }).catch(function (error) {
      // the attributes rode on a write that failed: hand them back, so the
      // caller's takeCarriedAttributes still sends them on their own
      if (sent) carryAttributes(sent);
      throw error;
    }).finally(function () {
      _protectionWrites -= 1;
    });
  }

  // The redesigned /cart summary renders the protection fee as its own row
  // server-side; after an auto-add the markup is stale until the next cart
  // mutation. Re-fetch just the footer section and swap its .js-contents so
  // Total and the fee row always agree. No-op off the cart page.
  function refreshCartPageSummary() {
    try {
      const footer = document.getElementById('main-cart-footer');
      if (!footer || !footer.dataset.id) return;
      fetch(window.location.pathname + '?sections=' + footer.dataset.id, { cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(function (sections) {
          const html = sections[footer.dataset.id];
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('.js-contents');
          const current = footer.querySelector('.js-contents');
          if (fresh && current) {
            current.innerHTML = fresh.innerHTML;
            hideShippingProtectionCartItems();
          }
        })
        .catch(function () { /* stale row until next mutation — acceptable */ });
    } catch (e) { /* never break the add path */ }
  }

  function onToggle() {
    if (window.CartOptimist) CartOptimist.optimisticToggleSP(_enabled);
    return syncCart();
  }

  function submitCheckoutControl(control) {
    var formId = control && control.getAttribute ? control.getAttribute('form') : null;
    var form = formId ? document.getElementById(formId) : null;

    if (!form) {
      form = (control && control.closest) ? control.closest('form') : null;
    }
    if (!form) {
      form = document.getElementById('cart') || document.getElementById('CartDrawer-Form');
    }

    if (!form) {
      // leaving without a submit event: mark the tap as in flight (cart-drawer.js)
      if (control && window.markCheckoutLeaving) window.markCheckoutLeaving(control);
      window.location.href = '/checkout';
      return;
    }

    form.querySelectorAll('.cart-item[data-product-handle*="shipping-protection" i], .cart-item[data-product-handle*="navidium" i]').forEach(function (item) {
      item.remove();
    });

    if (!form.querySelector('input[name="checkout"]')) {
      var checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = '1';
      form.appendChild(checkoutInput);
    }

    // form.submit() fires no submit event: mark the tap as in flight (cart-drawer.js)
    if (control && window.markCheckoutLeaving) window.markCheckoutLeaving(control);
    form.submit();
  }

  function bindCheckoutSyncGuard() {
    document.addEventListener('click', function (event) {
      var control = event.target && event.target.closest
        ? event.target.closest('button[name="checkout"], input[name="checkout"], #checkout, #CartDrawer-Checkout')
        : null;

      if (!control) return;
      if (!_syncing && !_syncQueued) return;
      if (_checkoutGuardBusy) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      _checkoutGuardBusy = true;
      // skip the rest of a verification pause, and show that the tap is held
      if (_verifyWake) _verifyWake();
      control.setAttribute('aria-busy', 'true');
      control.classList.add('cart-control-loading');
      /* The hold is bounded. Past CHECKOUT_HOLD_MS the tap goes ahead only
         when no protection write is unanswered (the stall is a read or the
         pause); with a write in flight it is dropped and the button is live
         again, because the form posts updates[] by line order and that write
         can change the lines under it (the instant cart's own rule,
         cart-instant.js). Only the settled race submits: nothing chained to
         the raw sync promise can navigate later. */
      let holdTimer = null;
      const holdExpired = new Promise(function (resolve) {
        holdTimer = setTimeout(function () { resolve('timeout'); }, CHECKOUT_HOLD_MS);
      });
      Promise.race([(_syncPromise || Promise.resolve()).catch(function () {}), holdExpired])
        .then(function (result) {
          clearTimeout(holdTimer);
          control.removeAttribute('aria-busy');
          control.classList.remove('cart-control-loading');
          if (result === 'timeout') {
            const outcome = _protectionWrites > 0 ? 'dropped' : 'released';
            try {
              console.warn('[ShippingProtection] checkout hold timed out:', outcome);
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({ event: 'sp_checkout_timeout', sp_outcome: outcome });
            } catch (e) { /* reporting never blocks checkout */ }
            if (outcome === 'dropped') return;
          }
          submitCheckoutControl(control);
        })
        .finally(function () {
          _checkoutGuardBusy = false;
          control.removeAttribute('aria-busy');
          control.classList.remove('cart-control-loading');
        });
    }, true);
  }

  /* ------------------------------------------------------------------ */
  /*  Init                                                               */
  /* ------------------------------------------------------------------ */

  function init() {
    ensureWidgetIntact();
    bindCheckoutSyncGuard();

    fetch('/products/' + PRODUCT_HANDLE + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('Product not found: ' + PRODUCT_HANDLE);
        return r.json();
      })
      .then(function (product) {
        _variants = product.variants.slice().sort(function (a, b) { return a.price - b.price; });
        ensureWidgetIntact();
        if (window._cartState) {
          adjustDisplayedSubtotal(window._cartState);
          hideShippingProtectionCartItems();
          updateWidgetVisibility(window._cartState);
        }
        syncCart();
      })
      .catch(function (err) {
        console.warn('[ShippingProtection] Could not load product, widget will be hidden:', err);
        document.querySelectorAll('.shipping-protection-slot').forEach(function (el) { el.style.display = 'none'; });
      });
  }

  /* ------------------------------------------------------------------ */
  /*  Public API                                                         */
  /* ------------------------------------------------------------------ */

  window.ShippingProtection = {
    init: init, syncCart: syncCart, isEnabled: isEnabled,
    computeProtectionDollars: computeProtectionDollars,
    computeProtectionCents: computeProtectionCents,
    protectionPriceText: protectionPriceText,
    getProductOnlyTotal: getProductOnlyTotal,
    updateWidgetPrice: updateWidgetPrice,
    updateWidgetVisibility: updateWidgetVisibility,
    adjustDisplayedSubtotal: adjustDisplayedSubtotal,
    findBestVariant: findBestVariant,
    hideCartItems: hideShippingProtectionCartItems,
    ensureWidget: ensureWidgetIntact,
    getProtectableProductTotal: getProtectableProductTotal,
    isProtectableLine: isProtectableLine,
    // resolves when the current add/swap/remove of the protection line is
    // done, so another cart write can wait its turn instead of racing it
    whenSynced: function () { return _syncPromise; },
    carryAttributes: carryAttributes,
    takeCarriedAttributes: takeCarriedAttributes,
  };

  window.getProductOnlyTotal = getProductOnlyTotal;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
